import React from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export type ResourceRequirement = {
  resource: string;
  required: number | null;
  have: number | null;
};

export type ResourceRequirementsFormValues = {
  resourceRequirements?: ResourceRequirement[];
};

const FIELD_NAME = 'resourceRequirements';

const EMPTY_REQUIREMENT: ResourceRequirement = {
  resource: '',
  required: null,
  have: null,
};

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

type RequirementRowProps = {
  index: number;
  onRemove: () => void;
};

const RequirementRow: React.FC<RequirementRowProps> = ({ index, onRemove }) => {
  const { register, control } =
    useFormContext<ResourceRequirementsFormValues>();
  const [required, have] = useWatch({
    control,
    name: [
      `${FIELD_NAME}.${index}.required`,
      `${FIELD_NAME}.${index}.have`,
    ] as const,
  });
  const need = toNumber(required) - toNumber(have);

  return (
    <TableRow>
      <TableCell>
        <Input
          aria-label={`Resource ${index + 1}`}
          {...register(`${FIELD_NAME}.${index}.resource`, { required: true })}
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          min={0}
          aria-label={`Required ${index + 1}`}
          {...register(`${FIELD_NAME}.${index}.required`, {
            valueAsNumber: true,
          })}
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          min={0}
          aria-label={`Have ${index + 1}`}
          {...register(`${FIELD_NAME}.${index}.have`, { valueAsNumber: true })}
        />
      </TableCell>
      <TableCell>{need}</TableCell>
      <TableCell>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Delete requirement ${index + 1}`}
          onClick={onRemove}
        >
          <Trash2 />
        </Button>
      </TableCell>
    </TableRow>
  );
};

const ResourceRequirements: React.FC = () => {
  const { control } = useFormContext<ResourceRequirementsFormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: FIELD_NAME,
  });

  return (
    <div className="mt-3 rounded border border-neutral-500 p-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <h4 className="tw:m-0 tw:text-xs tw:font-semibold tw:tracking-wide tw:uppercase tw:text-muted-foreground">
          Resource Requirements
        </h4>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => append({ ...EMPTY_REQUIREMENT })}
        >
          <Plus data-icon="inline-start" />
          Add requirement
        </Button>
      </div>
      {fields.length === 0 && (
        <p className="txt--sml text-weak mb-0">No resource requirements yet</p>
      )}
      {fields.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Resource</TableHead>
              <TableHead>Required</TableHead>
              <TableHead>Have</TableHead>
              <TableHead>Need</TableHead>
              <TableHead>
                <span className="visually-hidden">Delete</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {fields.map((field, index) => (
              <RequirementRow
                key={field.id}
                index={index}
                onRemove={() => remove(index)}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default ResourceRequirements;
